import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo
    })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-600">
                <AlertTriangle className="w-6 h-6" />
                <span>Si è verificato un errore</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-gray-600">
                <p className="mb-4">
                  Si è verificato un errore imprevisto. Questo potrebbe essere dovuto a:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Problemi di connessione al database</li>
                  <li>Permessi insufficienti per accedere ai dati</li>
                  <li>Configurazione mancante o errata</li>
                  <li>Errore temporaneo del sistema</li>
                </ul>
              </div>

              {this.state.error && (
                <details className="bg-gray-50 p-4 rounded border">
                  <summary className="cursor-pointer font-medium text-sm text-gray-700 mb-2">
                    Dettagli tecnici dell'errore
                  </summary>
                  <div className="text-xs font-mono text-gray-600 space-y-2">
                    <div>
                      <strong>Errore:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack trace:</strong>
                        <pre className="whitespace-pre-wrap mt-1 text-xs">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="flex space-x-4 pt-4">
                <Button onClick={this.handleReload} className="flex items-center space-x-2">
                  <RefreshCw className="w-4 h-4" />
                  <span>Ricarica Pagina</span>
                </Button>
                <Button 
                  variant="outline" 
                  onClick={this.handleGoHome}
                  className="flex items-center space-x-2"
                >
                  <Home className="w-4 h-4" />
                  <span>Torna alla Home</span>
                </Button>
              </div>

              <div className="text-xs text-gray-500 pt-4 border-t">
                <p>
                  Se il problema persiste, contatta l'amministratore del sistema.
                  Includi i dettagli tecnici dell'errore mostrati sopra.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook per uso funzionale
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}


